// Folder views - one generic grid+search renderer shared by every collection:
//   books      local library (image cards, paginated, favoritable)
//   worksheets web-app launcher from data/apps.json (icon cards, favoritable)
//   tools      first-party utilities (svg cards, opens /tools/<id> internally)
//   favorites  unified favorites (sites + books + apps), removable
//   history    recent sites (link cards, clearable, opens proxied)
// Searching re-renders the filtered (and paginated) slice rather than hiding
// 1300+ DOM nodes, so large libraries stay snappy.
import { announce as say } from "./announce.js";
import { state, active } from "./state.js";
import { folderTitle, folderSearch, folderCats, folderClear, folderEmpty, folderPager, gridEl, setAddr } from "./dom.js";
import { render } from "./view.js";
import { navigateTab } from "./navigation.js";
import { newTab, openInternal } from "./tabs.js";
import { openAppPage, isLinuxTab } from "./linux-open.js";
import { TOOLS } from "../shared/tools-manifest.js";
import * as account from "./account.js";
import * as favorites from "./favorites.js";
import * as history from "./history.js";
import * as bookRecents from "./book-recents.js";
import * as reviews from "./reviews.js";
import * as quests from "./quests.js";
import { renderLeaderboard } from "./leaderboard.js";
import { ALL_VIEW, ALL_LABEL, renderAllShortcuts } from "./all-shortcuts.js";
import { subscribe as onSettings, get as getSetting, setMany } from "../shared/settings-store.js";
import { pinToolPatch } from "../shared/shortcuts.js";
import { monogram, hostKey } from "../shared/site-icons.js";
import { icons, loadIcons } from "./icon-store.js";
import { bookCategory, categoryCounts } from "../shared/book-categories.js";
import { warmPlayPass } from "./play-pass.js";
import { canIsolate, NEEDS_OTHER_BROWSER } from "../shared/isolation.js";

// The Forums view is a lazy chunk (docs/HOME-2026-10.md, stage B budget): fetched the first
// time the view opens, then kept. It renders only if Forums is still the view on screen.
let forums = null;
function renderForums(el) {
	if (forums) return forums.renderForums(el, account);
	el.textContent = "Loading…";
	import(__RVM.forumsJs).then((m) => { forums = m; if (currentView === "forums") m.renderForums(el, account); })
		.catch(() => { if (currentView === "forums") el.textContent = "Couldn’t load the forums. Check your connection and try again."; });
}

const REMOVE_SVG = '<svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><line x1="4" y1="4" x2="10" y2="10"/><line x1="10" y1="4" x2="4" y2="10"/></svg>';
const STAR_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M12 3.6l2.5 5.1 5.6.8-4.05 3.95.96 5.55L12 16.9l-5.0 2.65.96-5.55L3.9 9.5l5.6-.8z"/></svg>';

let booksLoading = false;
let appsLoading = false;
let partnersLoading = false;
let currentView = null;
// The chosen category chip, per view (Books and Worksheets never share a filter). Books remembers
// the last chip on this device.
const activeCats = Object.create(null);
const CAT_KEY = "rv-books-cat";
function rememberedCat(view) {
	if (view !== "books") return "all";
	try { return localStorage.getItem(CAT_KEY) || "all"; } catch { return "all"; }
}
function setCat(view, c) {
	activeCats[view] = c;
	if (view === "books") { try { localStorage.setItem(CAT_KEY, c); } catch { /* private mode */ } }
}
const catOf = (view) => activeCats[view] || "all";
let booksSort = "default"; // "default" | "rated" (Top rated ranking)
let page = 0;

// --- small DOM + icon helpers --------------------------------------------
function div(cls, text) { const d = document.createElement("div"); d.className = cls; if (text != null) d.textContent = text; return d; }
function btn(cls, label, fn) { const b = document.createElement("button"); b.className = cls; b.textContent = label; b.addEventListener("click", fn); return b; }
// Site tiles show an icon saved on this device when the site was last open in a tab, else a
// monogram. They never fetch an icon from anywhere (see shared/site-icons.js).
function letterTile(name, host, cls) {
	const m = monogram(name, host);
	const d = div(cls || "rv-tile", m.letter);
	d.style.background = m.bg; d.style.color = m.fg;
	d.setAttribute("aria-hidden", "true");
	return d;
}
function iconImg(data, cls) {
	const img = new Image();
	img.alt = ""; img.decoding = "async";
	if (cls) img.className = cls;
	img.src = data; // a data: URL, so no request
	return img;
}
// Paints the stored icon for `host` into `wrap` once it is known, replacing the monogram.
function useStoredIcon(wrap, host, cls) {
	if (!host) return;
	const now = icons && icons.peek(host);
	if (now) { wrap.replaceChildren(iconImg(now, cls)); return; }
	loadIcons().then((m) => m && icons.lookup(host)).then((d) => { if (d) wrap.replaceChildren(iconImg(d, cls)); });
}
function favicon(url, name, wrapCls, tileCls) {
	const host = hostKey(url);
	const wrap = div(wrapCls);
	wrap.appendChild(letterTile(name, host, tileCls));
	useStoredIcon(wrap, host);
	return wrap;
}
// `sources` is a single URL or a priority list (e.g. [thumb, cover]). The name
// fallback is only shown while loading or if every source fails - once an image
// loads it's hidden, so it never bleeds through a letterboxed (contain) image.
function coverImg(sources, name, cls) {
	const list = (Array.isArray(sources) ? sources : [sources]).filter(Boolean);
	const wrap = div(cls || "rv-card-imgwrap");
	const fb = div("rv-card-fallback", name || "Untitled");
	wrap.appendChild(fb);
	if (list.length) {
		const img = document.createElement("img");
		img.className = "rv-card-img"; img.loading = "lazy"; img.decoding = "async"; img.alt = "";
		let i = 0;
		img.addEventListener("load", () => { fb.style.display = "none"; });
		img.addEventListener("error", () => { if (++i < list.length) img.src = list[i]; else { img.remove(); fb.style.display = ""; } });
		img.src = list[0];
		wrap.appendChild(img);
	}
	return wrap;
}

// A favorite ★ toggle for cards (books + apps). `item` is a favorites entry.
function favStar(item) {
	const b = document.createElement("button");
	b.className = "rv-fav-star"; b.type = "button"; b.innerHTML = STAR_SVG;
	b.setAttribute("aria-label", "Favorite" + (item.name ? ": " + item.name : ""));
	const sync = () => { const on = favorites.has(item); b.classList.toggle("on", on); b.setAttribute("aria-pressed", on ? "true" : "false"); b.title = on ? "Remove from favorites" : "Add to favorites"; };
	sync();
	b.addEventListener("click", (e) => { e.stopPropagation(); const wasOn = favorites.has(item); favorites.toggle(item); sync(); if (!wasOn && favorites.has(item)) quests.track("favorite"); });
	return b;
}

// --- card builders --------------------------------------------------------
// Shared media card used by BOTH Books and Worksheets - a square, uncropped
// image (cover or app icon) with the name below and a favorite ★. `voteBook`, when
// given (Books only), adds the star rating.
// A compact 5-star rating for a book. Shows the average (dim) or the signed-in user's
// own rating (bright), with the vote count. Clicking a star rates it; signed-out clicks
// nudge toward sign-in. Stops propagation so rating never opens the book.
function starBar(bookId, bookName) {
	const bar = div("rv-stars");
	bar.addEventListener("click", (e) => e.stopPropagation());
	// One Tab stop per book: arrows move between the stars, Enter or Space rates.
	const row = div("rv-stars-row");
	row.setAttribute("role", "group");
	row.setAttribute("aria-label", "Rate " + (bookName || "this book"));
	const count = div("rv-stars-count");
	const stars = [];
	let focusAt = 0;
	const rove = (n) => { focusAt = n; stars.forEach((s, j) => { s.tabIndex = j === n ? 0 : -1; }); };
	function paint() {
		const a = reviews.aggFor(bookId);
		const my = reviews.mineFor(bookId);
		const shown = my || (a ? Math.round(a.avg) : 0);
		for (let i = 0; i < 5; i++) {
			stars[i].classList.toggle("on", i < shown);
			stars[i].classList.toggle("mine", !!my && i < my);
			stars[i].setAttribute("aria-pressed", my === i + 1 ? "true" : "false");
		}
		bar.classList.toggle("rated", !!my);
		count.textContent = a && a.count ? a.avg + " (" + a.count + ")" : (reviews.isSignedIn() ? "Rate" : "");
		count.setAttribute("aria-label", a && a.count ? "Average " + a.avg + " stars from " + a.count + (a.count === 1 ? " rating" : " ratings") : "");
		if (!stars.some((s) => s === document.activeElement)) rove(my ? my - 1 : 0);
	}
	for (let i = 0; i < 5; i++) {
		const star = document.createElement("button");
		star.type = "button"; star.className = "rv-star"; star.innerHTML = STAR_SOLID;
		star.setAttribute("aria-label", (i + 1) + " star" + (i ? "s" : ""));
		star.addEventListener("click", async (e) => {
			e.stopPropagation();
			rove(i);
			if (!reviews.isSignedIn()) { toastMini("Sign in (Settings > Account and backup) to rate."); return; }
			const next = reviews.mineFor(bookId) === i + 1 ? 0 : i + 1; // click your current rating to clear
			paintPending(next);
			await reviews.rate(bookId, next);
			if (next > 0) quests.track("rate");
			paint();
		});
		star.addEventListener("keydown", (e) => {
			const to = { ArrowRight: focusAt + 1, ArrowUp: focusAt + 1, ArrowLeft: focusAt - 1, ArrowDown: focusAt - 1, Home: 0, End: 4 }[e.key];
			if (to == null) return;
			e.preventDefault(); e.stopPropagation();
			rove(Math.max(0, Math.min(4, to)));
			stars[focusAt].focus();
		});
		stars.push(star); row.appendChild(star);
	}
	function paintPending(n) { for (let i = 0; i < 5; i++) { stars[i].classList.toggle("on", i < n); stars[i].classList.toggle("mine", i < n); } }
	bar.append(row, count);
	paint();
	bar._paint = paint;
	return bar;
}

// A tiny transient toast (used by star sign-in nudge). One shared node.
let _miniToast = null, _miniTimer = 0;
function toastMini(msg) {
	if (!_miniToast) { _miniToast = div("rv-mini-toast"); _miniToast.setAttribute("aria-hidden", "true"); document.body.appendChild(_miniToast); }
	_miniToast.textContent = msg; _miniToast.hidden = false;
	say(msg);
	clearTimeout(_miniTimer); _miniTimer = setTimeout(() => { _miniToast.hidden = true; }, 2600);
}

const STAR_SOLID = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3.6l2.5 5.1 5.6.8-4.05 3.95.96 5.55L12 16.9l-5.0 2.65.96-5.55L3.9 9.5l5.6-.8z"/></svg>';

// The card's name is a real button, so cards work from the keyboard and screen readers. A click
// anywhere on the card still opens it (the button's click bubbles to the card handler).
function openBtn(cls, label) {
	const b = document.createElement("button");
	b.type = "button";
	b.className = cls + " rv-card-open";
	b.textContent = label;
	return b;
}

function imageCard(name, imageUrl, onOpen, favItem, cat, voteBook) {
	const card = div("rv-card");
	card.title = name || ""; card.dataset.search = ((name || "") + " " + (cat || "")).toLowerCase();
	if (cat) card.dataset.cat = cat;
	const wrap = imageUrl && imageUrl.nodeType ? imageUrl : coverImg(imageUrl, name);
	card.append(wrap, openBtn("rv-card-title", name || "Untitled"));
	if (favItem) card.appendChild(favStar(favItem)); // after the name, so Tab reaches the book first
	if (voteBook) card.appendChild(starBar(String(voteBook.id != null ? voteBook.id : voteBook.url), name));
	card.addEventListener("click", onOpen);
	return card;
}
// App cards use a local file from /data/img/apps/<slug>.png when there is one (crisp, works
// offline, and fixes apps whose site icon is wrong), else the icon saved when the app was last
// open, else a monogram. Only slugs the build found in that folder are used (__RVM.appIcons, see
// build.mjs), so a missing file never costs a 404.
const APP_ICON_DIR = "/data/img/apps/";
// Guarded: a bundle built without the define (an older build.mjs) must not throw at load.
const LOCAL_APP_ICONS = new Set((() => { try { return Array.isArray(__RVM.appIcons) ? __RVM.appIcons : []; } catch { return []; } })());
const appSlug = (name) => String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
function appCover(url, name, localIcon) {
	const slug = appSlug(name);
	const local = localIcon || (slug && LOCAL_APP_ICONS.has(slug) ? APP_ICON_DIR + slug + ".png" : "");
	if (local) return coverImg([local], name);
	const host = hostKey(url);
	const wrap = div("rv-card-imgwrap");
	const inner = div("rv-card-mono");
	inner.appendChild(letterTile(name, host, "rv-tile"));
	wrap.appendChild(inner);
	useStoredIcon(inner, host, "rv-card-img");
	return wrap;
}
function toolCard(tool, onOpen) {
	const card = div("rv-card rv-card-tool");
	card.title = tool.name; card.dataset.search = (tool.name + " " + (tool.blurb || "")).toLowerCase();
	const icon = div("rv-tool-icon"); icon.innerHTML = tool.icon;
	const blurb = div("rv-card-blurb", tool.blurb || "");
	blurb.id = "rv-card-blurb-" + tool.id;
	const open = openBtn("rv-card-title", tool.name);
	open.setAttribute("aria-describedby", blurb.id);
	// Pin to the start page: one tool at a time, so pinning another moves the pin.
	const pin = document.createElement("button");
	pin.type = "button"; pin.className = "rv-card-pin"; pin.dataset.id = tool.id; pin.innerHTML = PIN_SVG;
	paintPin(pin);
	pin.addEventListener("click", (e) => {
		e.stopPropagation();
		setMany(pinToolPatch(tool.id, getSetting));
		document.querySelectorAll(".rv-card-pin").forEach(paintPin);
		say(getSetting("pinTool") === tool.id ? tool.name + " is pinned to your start page." : tool.name + " is off your start page.");
	});
	card.append(icon, open, blurb, pin);
	card.addEventListener("click", onOpen);
	return card;
}
const PIN_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3h6l-1 6 4 4H6l4-4z"/><line x1="12" y1="13" x2="12" y2="21"/></svg>';
function paintPin(b) {
	const on = getSetting("pinTool") === b.dataset.id;
	const name = (TOOLS.find((t) => t.id === b.dataset.id) || {}).name;
	b.setAttribute("aria-pressed", String(on));
	b.setAttribute("aria-label", "Pin " + name + " to the start page");
	b.title = on ? "Pinned to the start page" : "Pin to the start page";
}
function linkCard(item, onOpen, onRemove, thumb) {
	const card = div("rv-card rv-card-link");
	const title = item.title || item.name || item.url;
	card.title = item.url || ""; card.dataset.search = ((title || "") + " " + (item.url || "")).toLowerCase();
	const text = div("rv-link-text");
	text.append(openBtn("rv-link-title", title), div("rv-link-url", item.sub || item.url || ""));
	card.append(thumb || favicon(item.url, title, "rv-link-fav", "rv-tile-sm"), text);
	if (onRemove) {
		const rm = document.createElement("button");
		rm.className = "rv-link-remove"; rm.type = "button"; rm.setAttribute("aria-label", "Remove " + title); rm.innerHTML = REMOVE_SVG;
		rm.addEventListener("click", (e) => { e.stopPropagation(); onRemove(); });
		card.append(rm);
	}
	card.addEventListener("click", onOpen);
	return card;
}

const openProxied = (url) => { const id = newTab(); navigateTab(id, url); };
// Partners open in a REAL browser tab (not proxied, not a BiB tab).
const openExternal = (url) => { try { window.open(url, "_blank", "noopener,noreferrer"); } catch (e) {} };
// Books may be local files (served at /books/, opened same-origin) or external
// URLs (opened through the proxy so they're tunnelled + cloaked).
// A title marked `isolate: true` in books.json needs a cross-origin isolated page (threads /
// SharedArrayBuffer). The flag is looked up by URL as well, so favorites, recents and reopened
// tabs (which carry only a URL) get it too.
export const needsIsolation = (g) => !!g && (g.isolate === true || (typeof g.url === "string" && Array.isArray(state.books) && state.books.some((b) => b && b.url === g.url && b.isolate === true)));
// A same-origin title in a tab. When this shell is not isolated (only possible once an in-page ad
// provider is allowed, docs/ADS-PLUMBING.md 1.2), an isolate title opens top-level instead, where
// /books/ keeps the isolation headers. Needs a user gesture; see main.js for the /?play= path.
// That top-level window opens at once (it needs the click), so the library pass is asked for ahead,
// when the Books view opens; if it is still missing, the game's own page asks and reloads.
export function openLocalBook(g) {
	if (needsIsolation(g) && self.crossOriginIsolated !== true) {
		// Safari cannot isolate it in a new tab either: keep it here and say why it may not run.
		if (!canIsolate()) toastMini(NEEDS_OTHER_BROWSER);
		else { warmPlayPass(); window.open(g.url, "_blank", "noopener"); return; }
	}
	openInternal(g.url, g.name || "Title");
}
export const openBook = (g) => {
	quests.track("book"); bookRecents.record(g);
	if (g.url && g.url.charAt(0) === "/") openLocalBook(g); else openProxied(g.url);
};
function openFavorite(it) {
	if (it.type === "book") openBook({ url: it.url, name: it.name, isolate: it.isolate === true });
	else if (it.page) openAppPage(it.page, it.name); // an internal Worksheets entry (e.g. Linux)
	else openProxied(it.url);
}
// A Worksheets entry either proxies a web app (`url`) or opens a first-party page (`page`, e.g.
// "/linux"); openAppPage() only accepts an allowlist of internal paths.
function openApp(a) {
	if (a.page) openAppPage(a.page, a.name);
	else openProxied(a.url);
	quests.track("worksheet");
}

// A favorites row - a book cover, app/site favicon, type label, and a remove ✕.
function favCard(it) {
	const title = it.title || it.name || it.url;
	const sub = it.type === "book" ? "Book" : it.type === "app" ? (it.category || "App") : (it.url || "");
	let thumb;
	if (it.type === "book") { thumb = coverImg(it.cover, title, "rv-link-fav rv-link-cover"); }
	else thumb = favicon(it.url, title, "rv-link-fav", "rv-tile-sm");
	return linkCard({ title, url: it.url, sub }, () => openFavorite(it), () => { favorites.remove(it); renderView("favorites"); }, thumb);
}

// --- view registry --------------------------------------------------------
const VIEWS = {
	books: {
		tab: "Books", title: "Books", style: "image", search: true, paged: true, pageSize: 60, sort: true, cats: true, fixedCats: true,
		items: () => state.books,
		loadingText: "Loading library…", emptyText: "No matching titles.",
		card: (g) => imageCard(g.name, [g.thumb, g.cover], () => openBook(g), { type: "book", url: g.url, name: g.name, cover: g.cover }, bookCategory(g), g),
	},
	worksheets: {
		tab: "Worksheets", title: "Worksheets", style: "image", search: true, cats: true,
		items: () => state.apps,
		loadingText: "Loading apps…", emptyText: "No matching apps.",
		card: (a) => imageCard(a.name, appCover(a.url, a.name, a.icon), () => openApp(a), a.page ? { type: "app", url: a.page, page: a.page, name: a.name, category: a.category } : { type: "app", url: a.url, name: a.name, category: a.category }, a.category),
	},
	partners: {
		tab: "Partners", title: "Partners", style: "image", search: true,
		items: () => state.partners,
		loadingText: "Loading partners…", emptyText: "No partners yet - add them in data/partners.json",
		card: (p) => imageCard(p.name, appCover(p.url, p.name, p.icon), () => openExternal(p.url), null),
	},
	tools: {
		tab: "Tools", title: "Tools", style: "tool", search: true,
		items: () => TOOLS,
		emptyText: "No matching tools.",
		card: (t) => toolCard(t, () => openInternal("/tools/" + t.id, t.name)),
	},
	forums: {
		tab: "Forums", title: "Forums", custom: true,
	},
	quests: {
		tab: "Quests", title: "Quests", custom: true,
	},
	leaderboard: {
		tab: "Leaderboard", title: "Leaderboard", custom: true,
	},
	[ALL_VIEW]: {
		tab: ALL_LABEL, title: ALL_LABEL, custom: true,
	},
	favorites: {
		tab: "Favorites", title: "Favorites", style: "link", search: true, clear: true,
		items: () => favorites.list(),
		emptyText: "No favorites yet - tap the ★ on a page, book, or app.",
		card: (it) => favCard(it),
		onClear: () => { if (confirm("Remove all favorites?")) { favorites.clear(); renderView("favorites"); } },
	},
	history: {
		tab: "History", title: "History", style: "link", search: true, clear: true,
		items: () => history.list(),
		emptyText: "No history yet.",
		card: (h) => linkCard(h, () => openProxied(h.url), () => { history.remove(h.url); renderView("history"); }),
		onClear: () => { if (confirm("Clear all history?")) { history.clear(); loadIcons().then((m) => m && icons.clear()); renderView("history"); } },
	},
};

const searchKey = (it) => ((it.name || it.title || "") + " " + (it.url || "") + " " + (it.category || it.blurb || "")).toLowerCase();

export function isFolderView(view) { return !!VIEWS[view]; }

export function openFolder(view, { focus = true } = {}) {
	if (!VIEWS[view]) return;
	// Never turn the Linux tab into a folder (its machine would keep running hidden and unguarded).
	if (isLinuxTab(active())) newTab();
	const t = active();
	if (!t) return;
	t.view = view; t.src = ""; t.internal = null; t.error = null; t.addr = ""; t.loading = false;
	t.title = VIEWS[view].tab;
	activeCats[view] = rememberedCat(view); page = 0;
	if (folderSearch) folderSearch.value = "";
	setAddr("");
	if (view === "books") { reviews.ensureAggregate(); reviews.ensureMine(); warmPlayPass(); } // star ratings + rankings, and a library pass ahead of the click
	if (view === "books" && state.books == null && !booksLoading) loadBooks();
	if (view === "worksheets" && state.apps == null && !appsLoading) loadApps();
	if (view === "forums") quests.track("forums");
	if (view === "partners" && state.partners == null && !partnersLoading) loadPartners();
	render();
	// Move focus to the new view's heading so keyboard and screen reader users land in it.
	if (focus && folderTitle) requestAnimationFrame(() => { try { folderTitle.focus({ preventScroll: true }); } catch (e) {} });
}

// Search results are announced after typing pauses, not on every keystroke.
let statusTimer = 0;
function announce(text) {
	const el = document.getElementById("rv-folder-status");
	if (!el) return;
	clearTimeout(statusTimer);
	statusTimer = setTimeout(() => { el.textContent = text; }, 700);
}

// Called by the render hub when the active folder view (re)appears.
export function renderFolderView(view) { renderView(view); }

function renderView(view) {
	const v = VIEWS[view];
	if (!v) return;
	currentView = view;
	// The library ad slot (slots/main.js) shows only under the Books and Worksheets grids; CSS keys
	// on this, so the slot node is never moved (moving an iframe would reload it).
	if (gridEl && gridEl.parentElement) gridEl.parentElement.dataset.view = view;
	// Custom views (forums) render their own DOM into the grid area and manage their
	// own internal navigation, so the folder chrome (title/search/cats/pager) steps
	// aside and the module takes the container.
	if (v.custom) {
		folderTitle.textContent = v.title;
		if (folderSearch) folderSearch.hidden = true;
		if (folderClear) folderClear.hidden = true;
		if (folderCats) { folderCats.hidden = true; folderCats.textContent = ""; }
		if (folderEmpty) folderEmpty.hidden = true;
		if (folderPager) { folderPager.hidden = true; folderPager.textContent = ""; }
		// Quests and the leaderboard can be all text; the scroll area itself takes focus so keyboard
		// users can scroll it.
		if (gridEl) { if (view === "forums" || view === ALL_VIEW) gridEl.removeAttribute("tabindex"); else gridEl.tabIndex = 0; }
		if (gridEl) {
			gridEl.dataset.style = "custom";
			if (view === "forums") renderForums(gridEl);
			else if (view === "quests") quests.renderQuests(gridEl);
			else if (view === "leaderboard") renderLeaderboard(gridEl);
			else if (view === ALL_VIEW) renderAllShortcuts(gridEl);
		}
		return;
	}
	folderTitle.textContent = v.title;
	if (folderSearch) folderSearch.hidden = !v.search;
	if (folderClear) folderClear.hidden = !v.clear;
	if (gridEl) { gridEl.dataset.style = v.style; gridEl.removeAttribute("tabindex"); }
	renderCats(v);

	const all = v.items();
	if (all == null) { folderEmpty.hidden = false; folderEmpty.textContent = v.loadingText || "Loading…"; gridEl.textContent = ""; renderPager(1); return; }

	const q = folderSearch && !folderSearch.hidden ? folderSearch.value.trim().toLowerCase() : "";
	let filtered = all;
	if (q) filtered = filtered.filter((it) => searchKey(it).indexOf(q) !== -1);
	const activeCat = catOf(view);
	if (v.cats && activeCat !== "all") {
		const catKey = v.fixedCats ? bookCategory : (it) => it.category || "Other";
		filtered = filtered.filter((it) => catKey(it) === activeCat);
	}
	if (v.sort && booksSort === "rated") {
		// Rank by average stars, descending; unrated books fall to the end in their
		// original order. reviews.aggFor keys on the book's id (fallback url), matching
		// the id starBar uses.
		const keyOf = (it) => String(it.id != null ? it.id : it.url);
		const score = (it) => { const a = reviews.aggFor(keyOf(it)); return a && a.count ? a.avg : -1; };
		filtered = filtered.slice().sort((x, y) => score(y) - score(x));
	}

	const size = v.paged ? v.pageSize : Math.max(1, filtered.length);
	const pages = Math.max(1, Math.ceil(filtered.length / size));
	if (page >= pages) page = pages - 1;
	if (page < 0) page = 0;
	const slice = v.paged ? filtered.slice(page * size, page * size + size) : filtered;

	folderEmpty.textContent = q ? "No matches." : v.emptyText || "Nothing here.";
	if (q) announce(filtered.length ? filtered.length + (filtered.length === 1 ? " result" : " results") : "No matches");
	folderEmpty.hidden = filtered.length > 0;
	if (folderClear && v.clear) folderClear.hidden = all.length === 0; // nothing to clear
	gridEl.textContent = "";
	const frag = document.createDocumentFragment();
	slice.forEach((it) => frag.appendChild(v.card(it)));
	gridEl.appendChild(frag);
	renderPager(v.paged ? pages : 1, filtered.length);
}

function renderPager(pages, total) {
	if (!folderPager) return;
	if (pages <= 1) { folderPager.hidden = true; folderPager.textContent = ""; return; }
	folderPager.hidden = false; folderPager.textContent = "";
	const prev = btn("rv-pager-btn", "‹ Prev", () => go(page - 1));
	prev.disabled = page === 0;
	const ind = div("rv-pager-ind", "Page " + (page + 1) + " of " + pages + " · " + total + " items");
	ind.setAttribute("role", "status");
	const next = btn("rv-pager-btn", "Next ›", () => go(page + 1));
	next.disabled = page >= pages - 1;
	folderPager.append(prev, ind, next);
}
function go(p) {
	const wasNext = p > page;
	page = p; renderView(currentView); if (gridEl) gridEl.scrollTop = 0;
	// The pager is rebuilt, so put focus back on the matching button (or the other one at an end).
	if (folderPager && folderPager.contains(document.activeElement) === false) {
		const btns = folderPager.querySelectorAll(".rv-pager-btn");
		const want = btns[wasNext ? 1 : 0];
		const target = want && !want.disabled ? want : btns[wasNext ? 0 : 1];
		if (target) target.focus();
	}
}

function chip(label, on, onClick, count) {
	const b = document.createElement("button");
	b.className = "rv-chip" + (on ? " active" : "");
	b.type = "button";
	b.setAttribute("aria-pressed", on ? "true" : "false");
	b.textContent = label;
	if (count != null) {
		const n = document.createElement("span");
		n.className = "rv-chip-n"; n.textContent = String(count);
		b.appendChild(n);
		b.setAttribute("aria-label", label + ", " + count + (count === 1 ? " title" : " titles"));
	}
	b.addEventListener("click", onClick);
	return b;
}

// Sort pills: normal order, or by star rating, highest first.
function sortPills() {
	const g = div("rv-chip-group");
	g.setAttribute("role", "group");
	g.setAttribute("aria-label", "Sort");
	[["default", "A to Z"], ["rated", "\u2605 Top rated"]].forEach(([mode, label]) => {
		g.appendChild(chip(label, booksSort === mode, () => { booksSort = mode; page = 0; renderView(currentView); }));
	});
	return g;
}

function renderCats(v) {
	if (!folderCats) return;
	if (!v.cats) { folderCats.hidden = true; folderCats.textContent = ""; folderCats.classList.remove("rv-cats-books"); return; }
	const view = currentView;
	const loaded = v.items();
	const items = loaded || [];
	let cats;
	if (v.fixedCats) {
		// Books: the shared category list in its fixed order, each with a count; empty ones hidden.
		// A remembered chip whose category no longer has titles falls back to All (only once the
		// list has loaded).
		cats = categoryCounts(items);
		if (loaded && catOf(view) !== "all" && !cats.some(([c]) => c === catOf(view))) setCat(view, "all");
	} else {
		cats = []; const seen = new Set();
		items.forEach((it) => { const c = it.category || "Other"; if (!seen.has(c)) { seen.add(c); cats.push([c, null]); } });
	}
	folderCats.hidden = false; folderCats.textContent = "";
	folderCats.classList.toggle("rv-cats-books", !!v.sort);
	folderCats.setAttribute("aria-label", v.sort ? "Categories and sort" : "Categories");
	const active = catOf(view);
	const g = div("rv-chip-group");
	g.setAttribute("role", "group");
	g.setAttribute("aria-label", "Categories");
	const pick = (c) => () => { setCat(view, c); page = 0; renderView(view); };
	g.appendChild(chip("All", active === "all", pick("all"), v.fixedCats && items.length ? items.length : null));
	cats.forEach(([c, n]) => g.appendChild(chip(c, active === c, pick(c), n)));
	folderCats.appendChild(g);
	if (v.sort) folderCats.appendChild(sortPills());
}

// --- loaders --------------------------------------------------------------
function loadBooks() {
	booksLoading = true;
	fetch("/books/books.json") // SW serves this network-first (fresh online, cached offline)
		.then((r) => (r.ok ? r.json() : []))
		.then((list) => {
			const seenUrl = new Set(), seenName = new Set();
			const nkey = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
			state.books = (Array.isArray(list) ? list : [])
				.filter((g) => g && g.url && !seenUrl.has(g.url) && seenUrl.add(g.url))
				.filter((g) => { const k = nkey(g.name); if (!k) return true; if (seenName.has(k)) return false; seenName.add(k); return true; })
				.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
		})
		.catch(() => { state.books = []; })
		.finally(() => { booksLoading = false; if (active() && active().view === "books") renderView("books"); });
}

function loadApps() {
	appsLoading = true;
	fetch("/data/apps.json")
		.then((r) => (r.ok ? r.json() : []))
		.then((list) => { state.apps = Array.isArray(list) ? list : []; })
		.catch(() => { state.apps = []; })
		.finally(() => { appsLoading = false; if (active() && active().view === "worksheets") renderView("worksheets"); });
}

function loadPartners() {
	partnersLoading = true;
	fetch("/data/partners.json")
		.then((r) => (r.ok ? r.json() : []))
		.then((list) => { state.partners = Array.isArray(list) ? list : []; })
		.catch(() => { state.partners = []; })
		.finally(() => { partnersLoading = false; if (active() && active().view === "partners") renderView("partners"); });
}

export function initFolderSearch() {
	if (folderSearch) folderSearch.addEventListener("input", () => { page = 0; renderView(currentView); });
	if (folderClear) folderClear.addEventListener("click", () => { const v = VIEWS[currentView]; if (v && v.onClear) v.onClear(); });
	// Refresh an open Favorites/History view when its store changes elsewhere.
	const refreshIf = (view) => () => { const a = active(); if (a && a.view === view) renderView(view); };
	favorites.subscribe(refreshIf("favorites"));
	history.subscribe(refreshIf("history"));
	reviews.onReviewsReady(refreshIf("books"));
	onSettings(refreshIf(ALL_VIEW));
}
